import { motion } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { hudCopy } from '../content';
import { HudPanel, MetricBar, ModuleHeader, ScannerOverlay, SystemFrame } from '../primitives';

const dependencyRows = [
  'Build Program',
  'Infrastructure Program',
  'Optimization Program',
  'Release Orchestration',
  'Quality Gate Controls'
] as const;

const dependencyColumns = ['Core API', 'Data Layer', 'AI Routing', 'Telemetry', 'Security'] as const;

const activeDependencies = new Set([
  '0-0',
  '0-1',
  '0-3',
  '0-4',
  '1-0',
  '1-1',
  '1-3',
  '1-4',
  '2-1',
  '2-2',
  '2-3',
  '3-0',
  '3-2',
  '3-4',
  '4-0',
  '4-1',
  '4-2',
  '4-4'
]);

export const ServiceArchitectureScreen = () => {
  const reducedMotion = useReducedMotion();

  return (
    <SystemFrame id="service-architecture" className="min-h-[calc(100vh-6.5rem)] pt-24">
      <HudPanel pulse className="relative">
        <ScannerOverlay tone="normal" />
        <ModuleHeader
          index="SCREEN 03"
          title={hudCopy.architecture.heading}
          subtitle={hudCopy.architecture.subtitle}
          tag="TOPOLOGY CONSOLE"
          status={hudCopy.microLabels.routeStable}
        />

        <HudPanel pulse className="relative mt-7 overflow-hidden bg-brand-panel-2/75">
          <p className="hud-micro text-brand-lime">Program Lanes</p>
          <div className="mt-4 space-y-4">
            {hudCopy.architecture.tracks.map((track, trackIndex) => (
              <article key={track.label} className="rounded-xl border border-brand-lime/22 bg-brand-panel/70 p-4">
                <div className="grid gap-4 lg:grid-cols-[15rem_1fr] lg:items-center">
                  <div>
                    <h3 className="text-base font-semibold text-brand-text">{track.label}</h3>
                    <div className="mt-3">
                      <MetricBar label="Track Integrity" value={84 - trackIndex * 6} />
                    </div>
                  </div>

                  <div className="relative">
                    <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-brand-lime/15 via-brand-lime/65 to-brand-lime/15" />
                    <div className="relative flex flex-wrap gap-3">
                      {track.items.map((item, itemIndex) => (
                        <div
                          key={item}
                          className="relative min-w-[9.4rem] flex-1 rounded-lg border border-brand-lime/20 bg-brand-panel-2/75 px-3 py-2"
                        >
                          <motion.span
                            className="absolute -top-1 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-brand-lime"
                            animate={reducedMotion ? undefined : { opacity: [0.35, 1, 0.45], scale: [0.8, 1.3, 0.9] }}
                            transition={{ duration: 1.6, repeat: Infinity, delay: itemIndex * 0.15 }}
                          />
                          <p className="text-sm text-brand-text-2">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <motion.div
            className="pointer-events-none absolute inset-y-0 left-[-18%] w-[18%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.25),transparent)]"
            animate={reducedMotion ? undefined : { x: ['0%', '650%'] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: 'linear' }}
          />
        </HudPanel>

        <HudPanel pulse className="relative mt-4 overflow-hidden bg-brand-panel-2/75">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="hud-micro text-brand-lime">Cross-System Dependency Matrix</p>
            <span className="hud-micro text-brand-aqua">LIVE ROUTING</span>
          </div>

          <div className="relative mt-4 space-y-2.5 sm:hidden">
            {dependencyRows.map((row, rowIndex) => (
              <motion.article
                key={`mobile-${row}`}
                className="relative overflow-hidden rounded-xl border border-brand-lime/22 bg-brand-panel/60 p-2.5"
                animate={reducedMotion ? undefined : { borderColor: ['rgba(183,255,106,0.22)', 'rgba(183,255,106,0.45)', 'rgba(183,255,106,0.22)'] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut', delay: rowIndex * 0.15 }}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="font-mono text-[0.56rem] uppercase tracking-[0.16em] text-brand-text-2">{row}</span>
                  <span className="hud-micro rounded border border-brand-aqua/30 bg-brand-panel-2/75 px-1.5 py-0.5 text-brand-aqua">
                    {dependencyColumns.filter((_, columnIndex) => activeDependencies.has(`${rowIndex}-${columnIndex}`)).length}/
                    {dependencyColumns.length}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  {dependencyColumns.map((column, columnIndex) => {
                    const isActive = activeDependencies.has(`${rowIndex}-${columnIndex}`);
                    return (
                      <motion.div
                        key={`mobile-${row}-${column}`}
                        className={`relative overflow-hidden rounded border px-2 py-1 ${
                          isActive ? 'border-brand-lime/38 bg-brand-panel-2/78' : 'border-brand-lime/14 bg-brand-panel/46'
                        }`}
                        animate={
                          reducedMotion
                            ? undefined
                            : isActive
                              ? {
                                  opacity: [0.5, 1, 0.55],
                                  borderColor: ['rgba(183,255,106,0.3)', 'rgba(183,255,106,0.72)', 'rgba(183,255,106,0.3)']
                                }
                              : { opacity: [0.28, 0.5, 0.3] }
                        }
                        transition={{
                          duration: isActive ? 2 + ((rowIndex + columnIndex) % 3) * 0.25 : 3.3,
                          repeat: Infinity,
                          ease: 'easeInOut',
                          delay: (rowIndex * 0.08 + columnIndex * 0.05) % 0.7
                        }}
                      >
                        <span className={`font-mono text-[0.5rem] uppercase tracking-[0.14em] ${isActive ? 'text-brand-aqua' : 'text-brand-text-3'}`}>
                          {column}
                        </span>
                        {isActive ? (
                          <motion.span
                            className="absolute inset-x-1 bottom-0.5 h-px bg-gradient-to-r from-transparent via-brand-lime/75 to-transparent"
                            animate={reducedMotion ? undefined : { opacity: [0.18, 0.85, 0.18] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                          />
                        ) : null}
                      </motion.div>
                    );
                  })}
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-4 hidden overflow-x-auto sm:block">
            <div className="relative min-w-[34rem] overflow-hidden rounded-xl border border-brand-lime/20 bg-brand-panel/55 p-2 sm:p-3">
              <div className="grid grid-cols-[9.4rem_repeat(5,minmax(0,1fr))] gap-2">
                <div className="hud-micro flex h-10 items-start pt-1 text-brand-text-3">SOURCE // TARGET</div>
                {dependencyColumns.map((column) => (
                  <div
                    key={column}
                    className="grid h-10 place-items-center rounded border border-brand-aqua/25 bg-brand-panel-2/75 px-2 text-center"
                  >
                    <span className="font-mono text-[0.56rem] uppercase tracking-[0.17em] text-brand-aqua">{column}</span>
                  </div>
                ))}

                {dependencyRows.map((row, rowIndex) => (
                  <div key={row} className="contents">
                    <div className="grid h-10 place-items-start rounded border border-brand-lime/20 bg-brand-panel-2/60 px-3 pt-2 text-left">
                      <span className="font-mono text-[0.56rem] uppercase tracking-[0.14em] text-brand-text-2">{row}</span>
                    </div>
                    {dependencyColumns.map((column, columnIndex) => {
                      const cellKey = `${rowIndex}-${columnIndex}`;
                      const isActive = activeDependencies.has(cellKey);
                      return (
                        <motion.div
                          key={`${row}-${column}`}
                          className={`relative h-10 overflow-hidden rounded border ${
                            isActive
                              ? 'border-brand-lime/35 bg-brand-panel-2/80'
                              : 'border-brand-lime/15 bg-brand-panel/45'
                          }`}
                          animate={
                            reducedMotion
                              ? undefined
                              : isActive
                                ? {
                                    opacity: [0.5, 1, 0.55],
                                    borderColor: [
                                      'rgba(183,255,106,0.26)',
                                      'rgba(183,255,106,0.75)',
                                      'rgba(183,255,106,0.26)'
                                    ]
                                  }
                                : { opacity: [0.25, 0.45, 0.28] }
                          }
                          transition={{
                            duration: isActive ? 2.2 + ((rowIndex + columnIndex) % 3) * 0.35 : 3.4,
                            repeat: Infinity,
                            ease: 'easeInOut',
                            delay: (rowIndex * 0.09 + columnIndex * 0.06) % 0.8
                          }}
                        >
                          {isActive ? (
                            <>
                              <motion.span
                                className="absolute left-1.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-brand-aqua shadow-[0_0_10px_rgba(88,247,182,0.75)]"
                                animate={reducedMotion ? undefined : { x: [0, 40, 0], opacity: [0.2, 1, 0.2] }}
                                transition={{
                                  duration: 2 + ((rowIndex * 2 + columnIndex) % 4) * 0.3,
                                  repeat: Infinity,
                                  ease: 'easeInOut',
                                  delay: (rowIndex * 0.12 + columnIndex * 0.07) % 0.9
                                }}
                              />
                              <motion.span
                                className="absolute inset-x-1 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-brand-lime/75 to-transparent"
                                animate={reducedMotion ? undefined : { opacity: [0.18, 0.85, 0.18] }}
                                transition={{
                                  duration: 1.6 + ((rowIndex + columnIndex) % 3) * 0.25,
                                  repeat: Infinity,
                                  ease: 'easeInOut'
                                }}
                              />
                            </>
                          ) : null}
                        </motion.div>
                      );
                    })}
                  </div>
                ))}
              </div>

              <motion.div
                className="pointer-events-none absolute inset-y-0 left-[-18%] w-[18%] bg-[linear-gradient(90deg,transparent,rgba(88,247,182,0.18),transparent)]"
                animate={reducedMotion ? undefined : { x: ['0%', '680%'] }}
                transition={{ duration: 4.6, repeat: Infinity, ease: 'linear' }}
              />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-3 text-[0.56rem] uppercase tracking-[0.16em] text-brand-text-3">
            <span className="rounded border border-brand-lime/25 px-2 py-1">18 Active Dependencies</span>
            <span className="rounded border border-brand-aqua/25 px-2 py-1">Adaptive Routing Signals</span>
          </div>
          <motion.div
            className="pointer-events-none absolute inset-y-0 left-[-20%] w-[20%] bg-[linear-gradient(90deg,transparent,rgba(88,247,182,0.25),transparent)]"
            animate={reducedMotion ? undefined : { x: ['0%', '620%'] }}
            transition={{ duration: 4.1, repeat: Infinity, ease: 'linear' }}
          />
        </HudPanel>
      </HudPanel>
    </SystemFrame>
  );
};
